'use client';

import { useCallback, useEffect, useState } from 'react';

type CopiedText = string | null;
export type CopyFn = (text: string) => Promise<boolean>;

export type CopyToClipboardHookReturnType = [CopiedText, CopyFn];

export function useCopyToClipboard(): CopyToClipboardHookReturnType {
  const [copied, setCopied] = useState<CopiedText>(null);
  const hasCopiedValue = Boolean(copied);
  const copy: CopyFn = useCallback(async (text) => {
    setCopied(null);

    if (!navigator?.clipboard) {
      return false;
    }

    // Try to save to clipboard then save it in the state if worked
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      return true;
    } catch {
      setCopied(null);
      return false;
    }
  }, []);

  useEffect(() => {
    let timeout: number;

    if (hasCopiedValue) {
      timeout = window.setTimeout(() => setCopied(null), 1_000);
    }

    return () => {
      window.clearTimeout(timeout);

      if (hasCopiedValue) {
        setCopied(null);
      }
    };
  }, [hasCopiedValue]);

  return [copied, copy];
}

export function useCopyToClipboardWithTooltip() {
  const [copiedText, onCopy] = useCopyToClipboard();
  const [forceTooltipOpen, setForceTooltipOpen] = useState(false);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const hasCopiedText = Boolean(copiedText);

  useEffect(() => {
    let timeout: number;

    if (hasCopiedText) {
      setForceTooltipOpen(true);

      timeout = window.setTimeout(() => {
        setForceTooltipOpen(false);
      }, 800);
    }

    return () => {
      window.clearTimeout(timeout);

      if (hasCopiedText) {
        setForceTooltipOpen(false);
      }
    };
  }, [hasCopiedText]);

  return {
    showCopiedTooltip,
    forceTooltipOpen,
    setShowCopiedTooltip,
    onCopy,
    copiedText,
  };
}

export type CopyToClipboardWithTooltipHookReturnType = ReturnType<typeof useCopyToClipboardWithTooltip>;
